/** UsersPanel — Parents/Staff management. Extracted from panels.jsx. */
import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, UserPlus } from "lucide-react";
import { Req } from "./_shared";

export function UsersPanel({ users, students = [], schools, reload, scope = "all" }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const defaultRole = scope === "parents" ? "parent" : scope === "staff" ? "teacher" : "teacher";
  const emptyForm = { role: defaultRole, email: "", name: "", phone: "", school_id: schools[0]?.id || "", classes: [], session: "" };
  const [form, setForm] = useState(emptyForm);

  const openCreate = () => { setEditing(null); setForm({ ...emptyForm, school_id: schools[0]?.id || "" }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ role: u.role, email: u.email, name: u.name, phone: u.phone || "", school_id: u.school_id || "", classes: u.classes || [], session: u.session || "" }); setOpen(true); };

  const save = async () => {
    const missing = [];
    if (!form.role) missing.push("Role");
    if (!form.name?.trim()) missing.push("Name");
    if (!form.email?.trim()) missing.push("Email");
    if (["teacher", "school_admin"].includes(form.role) && !form.school_id) missing.push("School");
    if (missing.length) {
      toast.error(`Please fill required field${missing.length > 1 ? "s" : ""}: ${missing.join(", ")}`);
      return;
    }
    try {
      if (editing) await http.put(`/users/${editing.id}`, form);
      else await http.post("/users", form);
      toast.success(editing ? "User updated" : "User added");
      setOpen(false); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete user?")) return;
    await http.delete(`/users/${id}`); toast.success("Deleted"); reload();
  };

  const addClass = () => setForm((f) => ({ ...f, classes: [...(f.classes || []), { grade: "", section: "" }] }));
  const updateClass = (idx, patch) => setForm((f) => {
    const next = [...(f.classes || [])];
    next[idx] = { ...next[idx], ...patch };
    return { ...f, classes: next };
  });
  const removeClass = (idx) => setForm((f) => ({ ...f, classes: (f.classes || []).filter((_, i) => i !== idx) }));

  const linkedStudentCount = useMemo(() => {
    if (!form.school_id || !form.classes?.length) return 0;
    return students.filter(
      (s) => s.school_id === form.school_id && form.classes.some((c) => c.grade === s.grade && c.section === s.section)
    ).length;
  }, [form.school_id, form.classes, students]);

  const schoolNameOf = (id) => schools.find((x) => x.id === id)?.name || "—";
  const scopeTitle = scope === "parents" ? "Parents" : scope === "staff" ? "Staff (Teachers, School Admins, Super Admins)" : "Users";

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-semibold text-lg">{scopeTitle} ({users.length})</h2>
        <Button data-testid={`add-user-btn-${scope}`} onClick={openCreate}><UserPlus className="w-4 h-4 mr-1" /> New {scope === "parents" ? "parent" : scope === "staff" ? "staff" : "user"}</Button>
      </div>
      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid={`admin-users-table-${scope}`}>
            <thead className="text-slate-400 uppercase text-[10px] tracking-widest bg-slate-50">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Phone</th>
                {scope === "staff" && <th className="text-left px-4 py-3">School</th>}
                {scope === "staff" && <th className="text-left px-4 py-3">Classes</th>}
                {scope === "staff" && <th className="text-left px-4 py-3">Session</th>}
                {scope === "parents" && <th className="text-left px-4 py-3">Kids</th>}
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-slate-100">
                  <td className="px-4 py-2.5 font-semibold text-slate-800">{u.name}</td>
                  <td className="px-4 py-2.5"><Badge className="uppercase tracking-widest text-[10px]">{u.role.replace("_", " ")}</Badge></td>
                  <td className="px-4 py-2.5 text-slate-600 text-xs">{u.email}</td>
                  <td className="px-4 py-2.5 text-slate-500 text-xs">{u.phone}</td>
                  {scope === "staff" && <td className="px-4 py-2.5 text-slate-600 text-xs">{u.school_id ? schoolNameOf(u.school_id) : <span className="text-slate-400 italic">—</span>}</td>}
                  {scope === "staff" && (
                    <td className="px-4 py-2.5 text-xs">
                      {u.classes?.length ? (
                        <div className="flex flex-wrap gap-1">
                          {u.classes.map((c, i) => <Badge key={i} variant="outline" className="text-[10px]">{c.grade}-{c.section}</Badge>)}
                        </div>
                      ) : <span className="text-slate-400 italic">All / N/A</span>}
                    </td>
                  )}
                  {scope === "staff" && <td className="px-4 py-2.5 text-slate-500 text-xs font-mono">{u.session || "—"}</td>}
                  {scope === "parents" && (
                    <td className="px-4 py-2.5">
                      <Badge variant="outline" className="tabular-nums">{u.linked_students ?? 0}</Badge>
                    </td>
                  )}
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <Button size="icon" variant="ghost" data-testid={`edit-user-${u.id}`} onClick={() => openEdit(u)}>
                      <Pencil className="w-4 h-4 text-slate-500" />
                    </Button>
                    <Button size="icon" variant="ghost" data-testid={`delete-user-${u.id}`} onClick={() => del(u.id)}>
                      <Trash2 className="w-4 h-4 text-rose-500" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit user" : `New ${scope === "parents" ? "parent" : "user"}`}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Role<Req /></Label>
              <select data-testid="user-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1 w-full h-10 border border-slate-200 rounded-md px-3 bg-white text-sm" disabled={!!editing}>
                <option value="teacher">Teacher (view-only)</option>
                <option value="school_admin">School Admin (data entry)</option>
                <option value="admin">Super Admin</option>
                <option value="parent">Parent</option>
              </select>
            </div>
            <div><Label>Name<Req /></Label><Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div className="col-span-2"><Label>Email<Req /></Label><Input data-testid="user-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
            <div><Label>Phone</Label><Input data-testid="user-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
            {form.role !== "parent" && (
              <div>
                <Label>School{["teacher", "school_admin"].includes(form.role) && <Req />}</Label>
                <select data-testid="user-school-select" value={form.school_id} onChange={(e) => setForm({ ...form, school_id: e.target.value })} className="mt-1 w-full h-10 border border-slate-200 rounded-md px-3 bg-white text-sm">
                  <option value="">—</option>
                  {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}
            {["teacher", "school_admin"].includes(form.role) && (
              <div className="col-span-2">
                <Label>Academic Session</Label>
                <Input
                  data-testid="user-session-input"
                  value={form.session}
                  onChange={(e) => setForm({ ...form, session: e.target.value })}
                  placeholder="e.g. 2026-27"
                  className="mt-1"
                />
              </div>
            )}
          </div>
          {["teacher", "school_admin"].includes(form.role) && (
            <div className="mt-4 p-4 rounded-lg border border-sky-200 bg-sky-50/40" data-testid="classes-assignment-block">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-widest text-sky-700 font-bold">Class assignments</Label>
                <Button size="sm" variant="outline" data-testid="add-class-btn" onClick={addClass} className="h-8 text-xs">
                  <Plus className="w-3 h-3 mr-1" /> Add class
                </Button>
              </div>
              <div className="space-y-2">
                {(form.classes || []).length === 0 && (
                  <div className="text-xs text-slate-500 italic">No classes assigned yet — this staff member won&apos;t see any students. Click &quot;Add class&quot; above.</div>
                )}
                {(form.classes || []).map((c, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`class-row-${i}`}>
                    <div className="flex-1">
                      <Input
                        data-testid={`class-grade-${i}`}
                        value={c.grade}
                        onChange={(e) => updateClass(i, { grade: e.target.value })}
                        placeholder="Grade (e.g. 5)"
                        className="h-9 text-sm"
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        data-testid={`class-section-${i}`}
                        value={c.section}
                        onChange={(e) => updateClass(i, { section: e.target.value })}
                        placeholder="Section (e.g. A)"
                        className="h-9 text-sm"
                      />
                    </div>
                    <Button size="icon" variant="ghost" data-testid={`remove-class-${i}`} onClick={() => removeClass(i)} className="h-9 w-9">
                      <Trash2 className="w-4 h-4 text-rose-500" />
                    </Button>
                  </div>
                ))}
              </div>
              {form.school_id && form.classes?.some((c) => c.grade && c.section) && (
                <div className="mt-3 pt-3 border-t border-sky-200/60 text-xs text-sky-700" data-testid="linked-students-preview">
                  <span className="font-bold">{linkedStudentCount}</span> student{linkedStudentCount === 1 ? "" : "s"} in <span className="font-semibold">{schoolNameOf(form.school_id)}</span> will be visible to this staff member.
                </div>
              )}
            </div>
          )}
          {editing && form.role === "parent" && (
            <div className="mt-3 p-3 rounded-lg border border-slate-200 bg-slate-50">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Linked children</div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {students.filter((s) => s.parent_email === editing.email).map((s) => (
                  <div key={s.id} className="text-xs flex items-center justify-between">
                    <span className="font-mono text-slate-600">{s.kid_id}</span>
                    <span className="text-slate-800 font-medium">{s.name}</span>
                  </div>
                ))}
                {students.filter((s) => s.parent_email === editing.email).length === 0 && (
                  <div className="text-xs text-slate-400 italic">No students linked yet.</div>
                )}
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={save} data-testid="user-save-btn">{editing ? "Update" : "Create"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
